import { Injectable } from '@angular/core';
import { lastValueFrom, ReplaySubject, Subject } from 'rxjs';
import { CarrierDeliveryNoteBundleOverview } from '../../../api/models/carrier-delivery-note-bundle-overview';
import { CarrierDeliveryNoteBundle } from '../../../api/models/carrier-delivery-note-bundle';
import { DeliveryNoteBundlesService } from '../../../api/services/delivery-note-bundles.service';
import { DeliveryNotesService } from '../../../api/services/delivery-notes.service';
import { TourResponse } from '../../../api/models/tour-response';
import { CarrierDeliveryNoteInPool } from '../../../api/models/carrier-delivery-note-in-pool';

@Injectable({
    providedIn: 'root',
})
export class TourPlanService {
    private tourPlanMode: boolean = false;
    private isEditMode: boolean = false;
    private tourEditKey: string = '';
    private tourEditExternalName: string = '';
    private tourPlanModeReplaySubject: ReplaySubject<boolean> =
        new ReplaySubject<boolean>(1);

    /**
     * Delivery notes for tour planning. The pool is needed to
     * @private
     */
    private deliveryNotesPool: CarrierDeliveryNoteInPool[] = [];
    private tourPlanDeliveryNotesReplaySubject: ReplaySubject<
        CarrierDeliveryNoteInPool[]
    > = new ReplaySubject<CarrierDeliveryNoteInPool[]>(1);

    private tourPlanDeliveryNotes: CarrierDeliveryNoteInPool[] = [];

    private deliveryNotesAddedSubject: Subject<CarrierDeliveryNoteInPool[]> =
        new Subject<CarrierDeliveryNoteInPool[]>();
    private deliveryNotesRemovedSubject: Subject<CarrierDeliveryNoteInPool[]> =
        new Subject<CarrierDeliveryNoteInPool[]>();

    private tourPlanBundles: CarrierDeliveryNoteBundle[] = [];
    private tourPlanBundleOverviews: CarrierDeliveryNoteBundleOverview[] = [];

    private bundlesAddedSubject: Subject<CarrierDeliveryNoteBundleOverview[]> =
        new Subject<CarrierDeliveryNoteBundleOverview[]>();
    private bundlesRemovedSubject: Subject<
        CarrierDeliveryNoteBundleOverview[]
    > = new Subject<CarrierDeliveryNoteBundleOverview[]>();

    constructor(
        private deliveryNoteBundleService: DeliveryNoteBundlesService,
        private deliveryNotesService: DeliveryNotesService
    ) {}

    public getTourPlanMode(): boolean {
        return this.tourPlanMode;
    }

    public getTourPlanDeliveryNotes() {
        return this.tourPlanDeliveryNotes;
    }

    public getTourPlanBundles() {
        return this.tourPlanBundles;
    }

    public getTourPlanModeReplaySubject(): ReplaySubject<boolean> {
        return this.tourPlanModeReplaySubject;
    }

    public getTourPlanDeliveryNotesReplaySubject() {
        return this.tourPlanDeliveryNotesReplaySubject;
    }

    public getTourDeliveryNotesAddedSubject() {
        return this.deliveryNotesAddedSubject;
    }
    public getTourDeliveryNotesRemovedSubject() {
        return this.deliveryNotesRemovedSubject;
    }

    public getTourBundlesAddedSubject() {
        return this.bundlesAddedSubject;
    }
    public getTourBundlesRemovedSubject() {
        return this.bundlesRemovedSubject;
    }

    public getIsEditMode(): boolean {
        return this.isEditMode;
    }

    public getTourEditKey(): string {
        return this.tourEditKey;
    }

    public getTourEditExternalName(): string {
        return this.tourEditExternalName;
    }

    /**
     * setting the plan mode (either active or inactive). If the active mode is set, the delivery notes pool is loaded
     * @param tourPlanMode
     * @param orgSiteKey
     * @param tour
     */
    public async setTourPlanMode(
        tourPlanMode: boolean,
        orgSiteKey?: string,
        tour?: TourResponse
    ) {
        if (tourPlanMode) {
            //fetch the delivery notes pool if planning mode has been activated
            const response = await lastValueFrom(
                this.deliveryNotesService.getCarrierDeliveryNotesMetadataInPool(
                    {
                        organizationSiteKey: orgSiteKey!,
                    }
                )
            );
            this.deliveryNotesPool = response;

            if (tour) {
                //edit mode has been activated
                this.prepareForEdit(tour);
            }
        } else {
            this.deliveryNotesPool = [];
        }
        this.tourPlanMode = tourPlanMode;
        this.tourPlanModeReplaySubject.next(this.tourPlanMode);
    }

    public addTourPlanDeliveryNotes(
        notes: CarrierDeliveryNoteInPool[]
    ): boolean {
        // Filter out delivery notes that are already in the tour plan (by key)
        const existingKeys = new Set(
            this.tourPlanDeliveryNotes.map(note => note._key)
        );
        const newNotes = notes.filter(note => !existingKeys.has(note._key));

        if (newNotes.length > 0) {
            this.tourPlanDeliveryNotes.push(...newNotes);
            this.deliveryNotesAddedSubject.next(newNotes);
            this.tourPlanDeliveryNotesReplaySubject.next(
                this.tourPlanDeliveryNotes
            );
        }

        return newNotes.length > 0;
    }

    public async syncDeliveryNotesWithBundles(
        deliveryNoteBundleOverviews: CarrierDeliveryNoteBundleOverview[],
        orgSiteKey: string
    ): Promise<CarrierDeliveryNoteBundleOverview[]> {
        let constructedPseudoBundles: CarrierDeliveryNoteInPool[][] = [];
        let deliveryNotesRunner = this.tourPlanDeliveryNotes.map(note => note);
        for (const deliveryNote of deliveryNotesRunner) {
            let notesWithSameBundle = deliveryNotesRunner.filter(
                note =>
                    note.deliveryNoteBundleKey ===
                    deliveryNote.deliveryNoteBundleKey
            );
            constructedPseudoBundles.push(notesWithSameBundle);
        }

        let promises: Promise<CarrierDeliveryNoteBundle>[] = [];
        constructedPseudoBundles.forEach(pseudoBundle => {
            if (pseudoBundle.length > 0) {
                let bundleId: string = pseudoBundle[0].deliveryNoteBundleKey;

                const bundleOverviewIndex: number =
                    deliveryNoteBundleOverviews.findIndex(
                        bundle => bundle._key === bundleId
                    );

                if (
                    bundleOverviewIndex != -1 &&
                    deliveryNoteBundleOverviews[bundleOverviewIndex]
                        .numberOfDeliveryNotes <= pseudoBundle.length
                ) {
                    const bundleOverview =
                        deliveryNoteBundleOverviews[bundleOverviewIndex];
                    promises.push(
                        lastValueFrom(
                            this.deliveryNoteBundleService.getCarrierBundle({
                                deliveryNoteBundleKey: bundleOverview._key,
                                organizationSiteKey: orgSiteKey,
                            })
                        )
                    );

                    deliveryNoteBundleOverviews.splice(bundleOverviewIndex, 1);
                }
            }
        });

        let carrierDeliveryNoteBundles: CarrierDeliveryNoteBundle[] = [];
        await Promise.all(promises).then(results => {
            results.forEach(result => {
                carrierDeliveryNoteBundles.push(result);
            });
        });

        this.tourPlanBundles.push(...carrierDeliveryNoteBundles);
        this.tourPlanBundleOverviews.push(...deliveryNoteBundleOverviews);

        return Promise.resolve(deliveryNoteBundleOverviews);
    }

    /**
     * remove delivery notes from the tour plan
     * @param notes
     */
    public removeTourPlanDeliveryNotes(notes: CarrierDeliveryNoteInPool[]) {
        let bundlesRemovedFromPlanning: CarrierDeliveryNoteBundle[] = [];
        //iterate over notes that are to be removed and find the corrosponding bundles
        for (const note of notes) {
            for (const bundle of this.tourPlanBundles) {
                if (
                    bundle.deliveryNotes.some(
                        deliveryNote => deliveryNote._key === note._key
                    )
                ) {
                    bundlesRemovedFromPlanning.push(bundle);
                }
            }
            this.tourPlanDeliveryNotes.splice(
                this.tourPlanDeliveryNotes.indexOf(note),
                1
            );
        }

        // restore the bundles that have been removed from the planning
        let bundleOverviewsRemovedFromPlanning: CarrierDeliveryNoteBundleOverview[] =
            [];
        bundlesRemovedFromPlanning.forEach(bundle => {
            let bundleOverviewIndex: number =
                this.tourPlanBundleOverviews.findIndex(
                    bundleOverview => bundleOverview._key === bundle._key
                );
            if (bundleOverviewIndex != -1) {
                bundleOverviewsRemovedFromPlanning.push(
                    this.tourPlanBundleOverviews[bundleOverviewIndex]
                );
                this.tourPlanBundleOverviews.splice(bundleOverviewIndex, 1);
            }

            this.tourPlanBundles.splice(
                this.tourPlanBundles.indexOf(bundle),
                1
            );
        });
        // emit the changes
        this.bundlesRemovedSubject.next(bundleOverviewsRemovedFromPlanning);

        this.deliveryNotesRemovedSubject.next(notes);
        this.tourPlanDeliveryNotesReplaySubject.next(
            this.tourPlanDeliveryNotes
        );
    }

    /**
     * add bundles (and all delivery notes from these bundles) to the tour plan
     * @param bundleKeys
     * @param orgSiteKey
     */
    public async addTourPlanBundles(
        bundleKeys: string[],
        orgSiteKey: string
    ): Promise<boolean> {
        // Filter out bundles that are already added to tour plan
        const existingBundleKeys = new Set(
            this.tourPlanBundles.map(bundle => bundle._key)
        );
        const newBundleKeys = bundleKeys.filter(
            key => !existingBundleKeys.has(key)
        );

        if (newBundleKeys.length === 0) {
            return false;
        }

        //download the bundle details to retrieve all delivery notes
        let promises: Promise<CarrierDeliveryNoteBundle>[] = [];
        for (const bundleKey of newBundleKeys) {
            promises.push(
                lastValueFrom(
                    this.deliveryNoteBundleService.getCarrierBundle({
                        deliveryNoteBundleKey: bundleKey,
                        organizationSiteKey: orgSiteKey,
                    })
                )
            );
        }
        let deliveryNotes: CarrierDeliveryNoteInPool[] = [];
        let carrierDeliveryNoteBundles: CarrierDeliveryNoteBundle[] = [];
        await Promise.all(promises).then(results => {
            results.forEach(result => {
                result.deliveryNotes.forEach(bundleDeliveryNote => {
                    const foundNote = this.deliveryNotesPool.find(
                        note => note._key === bundleDeliveryNote._key
                    );
                    if (foundNote) {
                        deliveryNotes.push(foundNote);
                    } else {
                        console.warn(
                            'Delivery note not found in pool:',
                            bundleDeliveryNote._key
                        );
                        // Transform bundle delivery note to CarrierDeliveryNoteInPool format
                        const transformedNote: CarrierDeliveryNoteInPool = {
                            _key: bundleDeliveryNote._key,
                            attachmentsCount:
                                bundleDeliveryNote.attachmentsCount || 0,
                            carrierKey: result.carrierKey || '',
                            carrierName: result.carrierName || '',
                            carrierNumberPlateIncoming:
                                bundleDeliveryNote.carrierNumberPlateIncoming ||
                                '',
                            carrierNumberPlateOutgoing:
                                result.carrierNumberPlateOutgoing || '',
                            checkinKey: '',
                            consigneeKey: result.consigneeKey || '',
                            consigneeLoadCarrierExchange:
                                bundleDeliveryNote.consigneeLoadCarrierExchange ||
                                false,
                            consigneeLoadCarrierImages:
                                bundleDeliveryNote.consigneeLoadCarrierImages ||
                                false,
                            consigneeName: result.consigneeName || '',
                            consignorKey: result.consignorKey || '',
                            consignorLoadCarrierExchange:
                                bundleDeliveryNote.consignorLoadCarrierExchange ||
                                false,
                            consignorLoadCarrierImages:
                                bundleDeliveryNote.consignorLoadCarrierImages ||
                                false,
                            consignorName: result.consignorName || '',
                            dateAnnounced:
                                bundleDeliveryNote.dateAnnounced || 0,
                            showDateAnnouncedTime:
                                bundleDeliveryNote.showDateAnnouncedTime ||
                                false,
                            dateCreated: bundleDeliveryNote.dateCreated || 0,
                            dateReceived: bundleDeliveryNote.dateReceived || 0,
                            dateShipped: result.dateShipped || 0,
                            deliveryNoteBundleKey: result._key,
                            deliveryNoteBundleName: result.name || '',
                            deliveryNoteBundleNumber: result.bundleNumber || '',
                            deliveryNoteBundleStatus: result.status as
                                | 'sent'
                                | 'closed',
                            discrepancies:
                                bundleDeliveryNote.discrepancies || false,
                            externalTourName: '',
                            fileName: bundleDeliveryNote.fileName || '',
                            fineDiscrepancies:
                                bundleDeliveryNote.fineDiscrepancies || false,
                            loadCarriers: bundleDeliveryNote.loadCarriers || [],
                            number: bundleDeliveryNote.number || '',
                            orderNumber: bundleDeliveryNote.orderNumber,
                            originalConsignor:
                                bundleDeliveryNote.originalConsignor,
                            originalConsignorGln:
                                bundleDeliveryNote.originalConsignorGln,
                            palletTickets:
                                bundleDeliveryNote.palletTickets || false,
                            partOfTour: false,
                            properties: bundleDeliveryNote.properties,
                            tourKey: '',
                            tourNumber: '',
                        };
                        deliveryNotes.push(transformedNote);
                    }
                });
                carrierDeliveryNoteBundles.push(result);
            });
        });
        //add the delivery notes to the tour plan
        this.addTourPlanDeliveryNotes(deliveryNotes);

        //notice the added bundles so the bundles table can remove these bundles from its datasource
        this.tourPlanBundles.push(...carrierDeliveryNoteBundles);

        // Create bundle overviews from the fetched bundle data
        const bundleOverviews = carrierDeliveryNoteBundles.map(bundle => ({
            _key: bundle._key,
            bundleNumber: bundle.bundleNumber,
            numberOfDeliveryNotes: bundle.deliveryNotes?.length || 0,
        }));

        this.tourPlanBundleOverviews.push(...(bundleOverviews as any));
        this.bundlesAddedSubject.next(bundleOverviews as any);

        return true;
    }

    /**
     * prepare for edit mode
     * @param tour
     * @private
     */
    private prepareForEdit(tour: TourResponse) {
        this.tourEditExternalName = tour.externalTourName;
        let notesToAdd: CarrierDeliveryNoteInPool[] = [];

        for (const key of tour.deliveryNoteKeys) {
            const noteFound = this.deliveryNotesPool.find(
                note => note._key === key
            );
            if (noteFound) {
                notesToAdd.push(noteFound);
            }
        }

        this.tourEditKey = tour._key;
        this.isEditMode = true;
        this.addTourPlanDeliveryNotes(notesToAdd);
    }

    /**
     * clears the planning
     */
    public clear(): void {
        this.tourPlanDeliveryNotes = [];
        this.tourPlanBundles = [];
        this.tourPlanBundleOverviews = [];
        this.tourPlanDeliveryNotesReplaySubject.next(
            this.tourPlanDeliveryNotes
        );
        this.isEditMode = false;
        this.tourEditExternalName = '';
    }
}
